<div data-testid="flash-sale-page" class="flash-sale-page">
  <p-toast />

  @if (detail(); as campaign) {
    <header class="flash-sale-header">
      <div>
        <a routerLink="/marketing/flash-sale" class="flash-sale-link">← {{ 'flash_sale.list.title' | translate }}</a>
        <h1 class="flash-sale-title">{{ campaign.campaignName }}</h1>
        <p class="flash-sale-subtitle">{{ scopeLabel(campaign) }}</p>
      </div>
      @if (canEdit()) {
        <button type="button" class="flash-sale-primary" (click)="openEdit(campaign)">
          {{ 'common.actions.edit' | translate }}
        </button>
      }
    </header>
    <section class="flash-sale-table-wrap">
      <table class="flash-sale-table">
        <thead>
          <tr>
            <th>{{ 'flash_sale.columns.sku' | translate }}</th>
            <th>{{ 'flash_sale.columns.amount' | translate }}</th>
            <th>{{ 'flash_sale.columns.quota' | translate }}</th>
            <th>{{ 'flash_sale.columns.stock' | translate }}</th>
          </tr>
        </thead>
        <tbody>
          @for (line of campaign.lines ?? []; track line.skuCode) {
            <tr>
              <td class="mono">{{ line.skuCode }}</td>
              <td>{{ line.amount | number: '1.0-0' }}</td>
              <td>{{ line.quotaRemaining | number: '1.0-0' }} / {{ line.quotaTotal | number: '1.0-0' }}</td>
              <td>{{ line.currentStock == null ? '—' : (line.currentStock | number: '1.0-0') }}</td>
            </tr>
          }
        </tbody>
      </table>
    </section>
  } @else {
    <header class="flash-sale-header">
      <div>
        <h1 class="flash-sale-title">{{ 'flash_sale.title' | translate }}</h1>
        <p class="flash-sale-subtitle">{{ 'flash_sale.subtitle' | translate }}</p>
      </div>
      @if (canCreate()) {
        <button type="button" class="flash-sale-primary" data-testid="flash-sale-add-btn" (click)="openCreate()">
          {{ 'flash_sale.actions.create' | translate }}
        </button>
      }
    </header>

    @if (alerts().length > 0) {
      <section class="flash-sale-alerts" aria-labelledby="flash-sale-alerts-title">
        <h2 id="flash-sale-alerts-title" class="flash-sale-alerts-title">
          {{ 'flash_sale.alerts.title' | translate }}
        </h2>
        <ul class="flash-sale-alert-list">
          @for (alert of alerts(); track alert.id) {
            <li class="flash-sale-alert">
              <div>
                <p class="flash-sale-alert-message">{{ alertDisplay(alert) }}</p>
              </div>
              <button type="button" class="flash-sale-link" (click)="dismissAlert(alert)">
                {{ 'flash_sale.alerts.dismiss' | translate }}
              </button>
            </li>
          }
        </ul>
      </section>
    }

    <section class="flash-sale-table-wrap">
      <div class="flash-sale-table-toolbar">
        <h2 class="flash-sale-section-title">{{ 'flash_sale.list.title' | translate }}</h2>
        <button type="button" class="flash-sale-secondary" [disabled]="loading()" (click)="reload(); loadAlerts()">
          {{ 'flash_sale.actions.refresh' | translate }}
        </button>
      </div>

      @if (loading()) {
        <p class="flash-sale-empty">{{ 'flash_sale.messages.loading' | translate }}</p>
      } @else if (rows().length === 0) {
        <p class="flash-sale-empty">{{ 'flash_sale.messages.empty' | translate }}</p>
      } @else {
        <div class="flash-sale-table-scroll">
          <table class="flash-sale-table">
            <thead>
              <tr>
                <th>{{ 'flash_sale.columns.campaign' | translate }}</th>
                <th>{{ 'flash_sale.columns.sku' | translate }}</th>
                <th>{{ 'flash_sale.columns.scope' | translate }}</th>
                <th>{{ 'flash_sale.columns.window' | translate }}</th>
                <th>{{ 'flash_sale.columns.quota_remaining' | translate }}</th>
                <th>{{ 'flash_sale.columns.status' | translate }}</th>
                <th>{{ 'flash_sale.columns.countdown' | translate }}</th>
                <th>{{ 'flash_sale.columns.actions' | translate }}</th>
              </tr>
            </thead>
            <tbody>
              @for (row of rows(); track row.id) {
                <tr>
                  <td>
                    <button type="button" class="flash-sale-link" (click)="openDetail(row)">
                      {{ row.campaignName }}
                    </button>
                  </td>
                  <td class="mono">{{ skuSummary(row) }}</td>
                  <td>{{ scopeLabel(row) }}</td>
                  <td>
                    <div class="flash-sale-window">
                      <span>{{ row.startAt | date: 'dd/MM/yyyy HH:mm' }}</span>
                      <span>→</span>
                      <span>{{ row.endAt | date: 'dd/MM/yyyy HH:mm' }}</span>
                    </div>
                  </td>
                  <td class="mono">{{ quotaRemainingSummary(row) }}</td>
                  <td>
                    <p-tag
                      [value]="('flash_sale.status.' + liveScheduleStatus(row)) | translate"
                      [severity]="scheduleSeverity(liveScheduleStatus(row))"
                    />
                  </td>
                  <td class="mono">
                    {{ liveCountdown(row) }}
                  </td>
                  <td>
                    <div class="flash-sale-row-actions">
                      @if (canEdit()) {
                        <button type="button" class="flash-sale-link" (click)="openEdit(row)">
                          {{ 'common.actions.edit' | translate }}
                        </button>
                      }
                      @if (canDelete()) {
                        <button type="button" class="flash-sale-link danger" (click)="remove(row)">
                          {{ 'common.actions.delete' | translate }}
                        </button>
                      }
                    </div>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
    </section>
  }

  <p-dialog
    [visible]="dialogVisible()"
    (visibleChange)="dialogVisible.set($event)"
    [modal]="true"
    [closable]="true"
    [draggable]="false"
    [resizable]="false"
    [style]="{ width: 'min(44rem, 96vw)' }"
  >
    <ng-template pTemplate="header">
      <h2 class="m-0 text-lg font-semibold text-slate-900">
        {{ (editingId() == null ? 'flash_sale.dialog.create' : 'flash_sale.dialog.edit') | translate }}
      </h2>
    </ng-template>

    <form class="flex flex-col gap-4" (ngSubmit)="$event.preventDefault(); save()">
      <label class="flex flex-col gap-1 text-sm text-slate-700">
        <span>
          {{ 'flash_sale.fields.campaign' | translate }}
          <span class="text-red-600">*</span>
        </span>
        <input
          pInputText
          class="w-full"
          [(ngModel)]="campaignName"
          name="campaignName"
          [attr.placeholder]="'flash_sale.fields.campaign_placeholder' | translate"
          (ngModelChange)="bumpForm()"
        />
      </label>

      <label class="flex flex-col gap-1 text-sm text-slate-700">
        <span>
          {{ 'flash_sale.fields.scope' | translate }}
          <span class="text-red-600">*</span>
        </span>
        <p-select
          [(ngModel)]="scopeType"
          name="scopeType"
          [options]="scopeOptions()"
          optionLabel="label"
          optionValue="value"
          appendTo="body"
          styleClass="w-full"
          (onChange)="onScopeChange()"
        />
      </label>

      @if (scopeType === 'SELECTED_STORES') {
        <label class="flex flex-col gap-1 text-sm text-slate-700">
          <span>
            {{ 'price_list.scope.stores' | translate }}
            <span class="text-red-600">*</span>
          </span>
          <p-multiSelect
            [(ngModel)]="selectedShopIds"
            name="shops"
            [options]="shopOptions()"
            optionLabel="name"
            optionValue="id"
            [filter]="true"
            display="chip"
            appendTo="body"
            styleClass="w-full"
            [placeholder]="'price_list.scope.stores_placeholder' | translate"
            (onChange)="onScopeOrSkuChange()"
          />
        </label>
      }

      <label class="flex flex-col gap-1 text-sm text-slate-700">
        <span>
          {{ 'price_list.start_date' | translate }}
          <span class="text-red-600">*</span>
        </span>
        <app-advanced-datepicker
          [(ngModel)]="startAt"
          name="startAt"
          mode="single"
          [showTime]="true"
          timeFormat="24h"
          dateFormat="DD/MM/YYYY"
          placeholder="dd/mm/yyyy HH:mm"
          [minDate]="minSelectableDate"
          [appendToBody]="true"
          class="w-full text-sm"
          (ngModelChange)="bumpForm()"
        />
      </label>

      <label class="flex flex-col gap-1 text-sm text-slate-700">
        <span>
          {{ 'price_list.end_date' | translate }}
          <span class="text-red-600">*</span>
        </span>
        <app-advanced-datepicker
          [(ngModel)]="endAt"
          name="endAt"
          mode="single"
          [showTime]="true"
          timeFormat="24h"
          dateFormat="DD/MM/YYYY"
          placeholder="dd/mm/yyyy HH:mm"
          [minDate]="minSelectableDate"
          [appendToBody]="true"
          class="w-full text-sm"
          (ngModelChange)="bumpForm()"
        />
        @if (
          editingId() == null &&
          startAt &&
          startAt.getTime() < minSelectableDate.getTime() - 60_000
        ) {
          <span class="text-xs text-red-600">{{ 'flash_sale.messages.start_not_past' | translate }}</span>
        }
        @if (startAt && endAt && endAt.getTime() <= startAt.getTime()) {
          <span class="text-xs text-red-600">{{ 'flash_sale.messages.end_before_start' | translate }}</span>
        }
      </label>

      <div class="flex flex-col gap-2">
        <div class="flex items-center justify-between gap-2">
          <h3 class="m-0 text-sm font-semibold text-slate-800">
            {{ 'flash_sale.fields.lines' | translate }}
            <span class="text-red-600">*</span>
          </h3>
          <button type="button" class="flash-sale-secondary" (click)="addLine()">
            {{ 'flash_sale.actions.add_sku' | translate }}
          </button>
        </div>

        <div class="flash-sale-lines-scroll rounded-md border border-slate-200">
          <table class="flash-sale-table w-full text-sm">
            <thead>
              <tr>
                <th>{{ 'flash_sale.fields.sku' | translate }}</th>
                <th>{{ 'flash_sale.fields.amount' | translate }}</th>
                <th>{{ 'flash_sale.fields.quota' | translate }}</th>
                <th>{{ 'flash_sale.columns.stock' | translate }}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              @if (lines.length === 0) {
                <tr>
                  <td colspan="5" class="flash-sale-lines-empty px-3 py-6 text-slate-500">
                    {{ 'flash_sale.messages.lines_empty' | translate }}
                  </td>
                </tr>
              } @else {
                @for (line of lines; track $index; let i = $index) {
                  <tr>
                    <td class="min-w-[14rem]">
                      <p-select
                        [(ngModel)]="line.skuCode"
                        [name]="'sku' + i"
                        [options]="skuOptions()"
                        optionLabel="skuName"
                        optionValue="skuCode"
                        [filter]="true"
                        [showClear]="true"
                        filterBy="skuName,skuCode"
                        appendTo="body"
                        styleClass="w-full"
                        [placeholder]="'flash_sale.fields.sku_placeholder' | translate"
                        (onChange)="onScopeOrSkuChange()"
                      >
                        <ng-template #item let-sku>
                          <span class="flex flex-col py-1">
                            <span>{{ sku.skuName }}</span>
                            <span class="text-xs text-slate-500">{{ sku.skuCode }}</span>
                          </span>
                        </ng-template>
                      </p-select>
                    </td>
                    <td>
                      <p-inputNumber
                        [(ngModel)]="line.amount"
                        [name]="'amount' + i"
                        [min]="0"
                        [useGrouping]="true"
                        styleClass="w-full"
                        (ngModelChange)="bumpForm()"
                      />
                    </td>
                    <td>
                      <p-inputNumber
                        [(ngModel)]="line.quota"
                        [name]="'quota' + i"
                        [min]="1"
                        [useGrouping]="true"
                        styleClass="w-full"
                        (ngModelChange)="bumpForm()"
                      />
                    </td>
                    <td class="mono whitespace-nowrap text-slate-600">
                      {{ line.currentStock == null ? '—' : (line.currentStock | number: '1.0-0') }}
                      <span class="text-slate-400">
                        / {{ line.maxAllowedQuota == null ? '—' : (line.maxAllowedQuota | number: '1.0-0') }}
                      </span>
                    </td>
                    <td>
                      <button
                        type="button"
                        class="flash-sale-link danger"
                        (click)="removeLine(i)"
                      >
                        {{ 'common.actions.delete' | translate }}
                      </button>
                    </td>
                  </tr>
                }
              }
            </tbody>
          </table>
        </div>
      </div>

      <div class="flex justify-end gap-2 pt-1">
        <button type="button" class="flash-sale-secondary" (click)="closeDialog()">
          {{ 'common.actions.cancel' | translate }}
        </button>
        <button type="submit" class="flash-sale-primary" [disabled]="!canSave()">
          {{ 'common.actions.save' | translate }}
        </button>
      </div>
    </form>
  </p-dialog>
</div>
